import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import {
  getUserCompanies,
  updateCompany,
  updateOrganization,
  uploadCompanyLogo,
} from "../api/user";
import {
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  XMarkIcon,
  CheckIcon,
  GlobeAltIcon,
  PhotoIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import CompanyAvatar from "../components/CompanyAvatar";
import PhoneInput, { isValidPhone } from "../components/PhoneInput";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { AnimatedModal, Input, Button, GridSkeleton } from "../components/ui";

// ── Plan display helpers ──────────────────────────────────────────────────────
const PLAN_LABELS = {
  FREE: "Free",
  STANDARD: "Standard",
  STANDARD_IN: "Standard",
  BUSINESS: "Business",
  BUSINESS_IN: "Business",
  BUSINESS_PRO: "Business Pro",
  BUSINESS_PRO_IN: "Business Pro",
  GROUP_STARTER: "Group Starter",
  GROUP_STARTER_IN: "Group Starter",
  GROUP_PRO: "Group Pro",
  GROUP_PRO_IN: "Group Pro",
  ENTERPRISE_HOLDING: "Enterprise",
  ENTERPRISE_HOLDING_IN: "Enterprise",
};

const PLAN_COLORS = {
  FREE: "bg-gray-100 text-gray-600",
  STANDARD: "bg-blue-100 text-blue-700",
  STANDARD_IN: "bg-blue-100 text-blue-700",
  BUSINESS: "bg-indigo-100 text-indigo-700",
  BUSINESS_IN: "bg-indigo-100 text-indigo-700",
  BUSINESS_PRO: "bg-purple-100 text-purple-700",
  BUSINESS_PRO_IN: "bg-purple-100 text-purple-700",
  GROUP_STARTER: "bg-teal-100 text-teal-700",
  GROUP_STARTER_IN: "bg-teal-100 text-teal-700",
  GROUP_PRO: "bg-emerald-100 text-emerald-700",
  GROUP_PRO_IN: "bg-emerald-100 text-emerald-700",
  ENTERPRISE_HOLDING: "bg-amber-100 text-amber-700",
  ENTERPRISE_HOLDING_IN: "bg-amber-100 text-amber-700",
};

const editSchema = Yup.object({
  name: Yup.string().required("Company name is required"),
  address: Yup.string().notRequired(),
  phone: Yup.string()
    .test(
      "phone",
      "Enter a valid phone number (6–15 digits)",
      (v) => !v || isValidPhone(v),
    )
    .notRequired(),
});

const LOGO_MAX_SIZE = 500 * 1024;

const EditCompanyModal = ({ company, isOpen, onClose, onSaved }) => {
  const [logoFile, setLogoFile] = React.useState(null);
  const [logoPreview, setLogoPreview] = React.useState(company?.logo);
  const [uploadingLogo, setUploadingLogo] = React.useState(false);

  React.useEffect(() => {
    const url = company?.logoUrl || company?.logo;
    if (url) setLogoPreview(url);
    else setLogoPreview(null);
  }, [company?.logoUrl, company?.logo]);

  if (!company) return null;

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > LOGO_MAX_SIZE) {
      toast.error("Logo must be 500KB or smaller");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Logo must be an image (JPEG, PNG, GIF, WebP)");
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  return (
    <AnimatedModal
      isOpen={isOpen && !!company}
      onClose={onClose}
      closeOnBackdrop
      contentClassName="bg-white shadow-xl w-full max-w-md overflow-hidden !rounded-2xl"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">Edit Company</h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <Formik
        initialValues={{
          name: company?.name || "",
          address: company?.address || "",
          phone: company?.phone || "",
        }}
        validationSchema={editSchema}
        enableReinitialize
        onSubmit={async (values, { setSubmitting, setFieldError }) => {
          try {
            let logoKey = company.logo;
            let logoUrl = company.logoUrl || company.logo;
            if (logoFile) {
              setUploadingLogo(true);
              const res = await uploadCompanyLogo(company._id, logoFile);
              logoKey = res?.data?.logo;
              logoUrl = res?.data?.logoUrl || logoUrl;
            }
            await updateCompany(company._id, { ...values, logo: logoKey });
            toast.success("Company updated successfully");
            onSaved({ ...company, ...values, logo: logoKey, logoUrl });
            onClose();
          } catch (err) {
            const msg = err?.message || "Failed to update company";
            setFieldError("name", msg);
            toast.error(msg);
          } finally {
            setSubmitting(false);
            setUploadingLogo(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form className="px-5 py-4 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Company Logo{" "}
                <span className="text-gray-400 font-normal">
                  (optional, max 500KB)
                </span>
              </label>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <PhotoIcon className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleLogoChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
                  />
                </div>
              </div>
            </div>
            <Input
              label="Company Name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g. Acme Ltd."
              required
              error={errors.name && touched.name ? errors.name : undefined}
              size="sm"
            />
            <Input
              label="Address"
              name="address"
              value={values.address}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Company address (optional)"
              size="sm"
            />
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone
              </label>
              <PhoneInput
                name="phone"
                value={values.phone}
                onChange={(v) => setFieldValue("phone", v)}
                onBlur={handleBlur}
                error={errors.phone && touched.phone}
              />
              {errors.phone && touched.phone && (
                <p className="mt-1.5 text-xs font-medium text-red-600">
                  {errors.phone}
                </p>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                fullWidth
                loading={isSubmitting}
                leftIcon={CheckIcon}
              >
                Save Changes
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </AnimatedModal>
  );
};

const CompanyCard = ({ company, isOrgAdmin, onEdit }) => {
  const roleName = !isOrgAdmin
    ? company.membership?.role?.name ||
      (company.membership?.companyRole === "MEMBER"
        ? "Member"
        : company.membership?.companyRole) ||
      "Member"
    : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <CompanyAvatar company={company} size="md" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900 truncate">
              {company.name}
            </p>
            {isOrgAdmin ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full mt-0.5">
                <CheckIcon className="w-3 h-3" />
                Admin
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full mt-0.5">
                {roleName}
              </span>
            )}
          </div>
        </div>
        {isOrgAdmin && onEdit && (
          <button
            type="button"
            onClick={() => onEdit(company)}
            title="Edit company"
            className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 shrink-0 transition-colors"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {company.address ? (
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <GlobeAltIcon className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
            <span className="break-words">{company.address}</span>
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">No address on file</p>
        )}
        {company.phone && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <svg
              className="w-4 h-4 shrink-0 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
              />
            </svg>
            <span>{company.phone}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const SUB_TABS = {
  organization: "organization",
  companies: "companies",
};

const OrganizationCompanies = () => {
  const { user, organization, setOrganization } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState(SUB_TABS.organization);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingOrgName, setEditingOrgName] = useState(false);
  const [orgNameValue, setOrgNameValue] = useState(organization?.name || "");
  const [savingOrgName, setSavingOrgName] = useState(false);

  const isOrgOwner = !!user?.organizationId;
  const planId = organization?.planId;
  const planLabel = PLAN_LABELS[planId] || planId || "—";
  const planColorClass = PLAN_COLORS[planId] || "bg-gray-100 text-gray-600";
  const paymentCountry = organization?.payment_country;

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    setOrgNameValue(organization?.name || "");
  }, [organization?.name]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const res = await getUserCompanies();
      setCompanies(res?.data?.companies || []);
    } catch (err) {
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = (updated) => {
    setCompanies((prev) =>
      prev.map((c) => (c._id === updated._id ? { ...c, ...updated } : c)),
    );
  };

  const ownedCompanies = companies.filter((c) => c.isOrgAdmin);
  const invitedCompanies = companies.filter((c) => !c.isOrgAdmin);

  if (!isOrgOwner) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
        <BuildingOfficeIcon className="w-12 h-12 text-gray-300 mb-4" />
        <h2 className="text-base font-semibold text-gray-700">
          Access Restricted
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Only organization admins can manage company details.
        </p>
      </div>
    );
  }

  const sidebarLinks = [
    {
      value: SUB_TABS.organization,
      label: "Organization",
      icon: BuildingOfficeIcon,
    },
    {
      value: SUB_TABS.companies,
      label: "Companies",
      icon: BuildingStorefrontIcon,
    },
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-screen">
      {/* ── Vertical sidebar: Navigation links ── */}
      <aside className="w-full lg:w-60 shrink-0 lg:border-r px-4 py-4 border-gray-200 lg:rounded-none h-screen">
        <nav className="space-y-1">
          {sidebarLinks.map((link) => {
            const Icon = link.icon;
            const isActive = activeSubTab === link.value;
            return (
              <button
                key={link.value}
                type="button"
                onClick={() => setActiveSubTab(link.value)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {link.label}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ── Main content: Organization or Companies ── */}
      <div className="flex-1 min-w-0 p-4">
        {activeSubTab === SUB_TABS.organization && (
          <div className="space-y-6">
            <div>
            <h2 className="text-base font-semibold text-gray-900">
              Organization Details
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
                View and edit your organization information
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-2xl">
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Name
                  </p>
                  {editingOrgName ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        value={orgNameValue}
                        onChange={(e) => setOrgNameValue(e.target.value)}
                        placeholder="Organization name"
                        size="sm"
                        className="max-w-xs"
                      />
                      <Button
                        size="sm"
                        loading={savingOrgName}
                        disabled={!orgNameValue.trim()}
                        onClick={async () => {
                          if (!orgNameValue.trim()) return;
                          setSavingOrgName(true);
                          try {
                            const res = await updateOrganization({
                              name: orgNameValue.trim(),
                            });
                            const updatedOrg = res?.data;
                            setOrganization((prev) =>
                              prev ? { ...prev, name: updatedOrg?.name } : null,
                            );
                            setEditingOrgName(false);
                            toast.success("Organization name updated");
                          } catch (err) {
                            toast.error(
                              err?.response?.data?.message ||
                                "Failed to update",
                            );
                          } finally {
                            setSavingOrgName(false);
                          }
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={savingOrgName}
                        onClick={() => {
                          setEditingOrgName(false);
                          setOrgNameValue(organization?.name || "");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group">
                      <p className="text-base font-semibold text-gray-900">
                        {organization?.name || "—"}
                      </p>
                      <button
                        type="button"
                        onClick={() => setEditingOrgName(true)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                        title="Edit name"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Plan
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${planColorClass}`}
                    >
                      {planLabel}
                    </span>
                    {organization?.isSubscribed && (
                      <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        Active
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Currency
                  </p>
                  <p className="text-sm text-gray-700">
                    {paymentCountry === "IN"
                      ? "INR"
                      : paymentCountry === "US"
                        ? "USD"
                        : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Companies
                  </p>
                  <p className="text-sm text-gray-700">
                    {ownedCompanies.length}{" "}
                    {ownedCompanies.length === 1 ? "company" : "companies"}{" "}
                    owned
                    {organization?.allowedCompanies &&
                    organization.allowedCompanies !== -1
                      ? ` / ${organization.allowedCompanies} allowed`
                      : ""}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === SUB_TABS.companies && (
          <>
            <div className="mb-4">
              <h2 className="text-base font-semibold text-gray-900">Companies</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Manage your companies and invited organizations
              </p>
            </div>
            {loading ? (
              <GridSkeleton count={6} viewMode="grid" />
            ) : (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      My Companies
                    </h2>
                    <span className="text-xs text-gray-400">
                      {ownedCompanies.length}
                      {organization?.allowedCompanies &&
                      organization.allowedCompanies !== -1
                        ? ` / ${organization.allowedCompanies} allowed`
                        : ""}
                    </span>
                  </div>
                  {ownedCompanies.length === 0 ? (
                    <div className="text-center py-10 bg-white border border-dashed border-gray-300 rounded-xl">
                      <BuildingOfficeIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 font-medium">
                        No companies yet
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        Use the Company Switcher in the sidebar to add your
                        first company.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {ownedCompanies.map((company) => (
                        <CompanyCard
                          key={company._id}
                          company={company}
                          isOrgAdmin={true}
                          onEdit={setEditingCompany}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {invitedCompanies.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                        Invited
                      </h2>
                      <span className="text-xs text-gray-400">
                        {invitedCompanies.length}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {invitedCompanies.map((company) => (
                        <CompanyCard
                          key={company._id}
                          company={company}
                          isOrgAdmin={false}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <EditCompanyModal
        company={editingCompany}
        isOpen={!!editingCompany}
        onClose={() => setEditingCompany(null)}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default OrganizationCompanies;
